import { useEffect, useState } from "react";
import { api, uploadFile } from "@/lib/api";
import { PageHeader, StatusBadge, FileLink } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, FolderArchive, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "§43 IfSG Belehrung", "Hygiene-Schulung", "Allergenmatrix",
  "Schädlingsmonitoring", "Lieferscheine", "Sicherheitsdatenblätter (SDB)",
];

function fmtDate(d) { if (!d) return null; try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; } }

function expiryInfo(expiry) {
  if (!expiry) return null;
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { variant: "warning", text: `abgelaufen (${fmtDate(expiry)})` };
  if (days <= 30) return { variant: "due", text: `läuft in ${days} T. ab` };
  return { variant: "done", text: `gültig bis ${fmtDate(expiry)}` };
}

export default function DokumentePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ category: CATEGORIES[0], title: "", issue_date: "", expiry_date: "", note: "" });

  const load = () => api.get("/documents").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    setBusy(true);
    try {
      let fileId = null;
      if (file) { const res = await uploadFile(file); fileId = res.file_id; }
      await api.post("/documents", {
        category: form.category, title: form.title, file_id: fileId,
        issue_date: form.issue_date || null, expiry_date: form.expiry_date || null, note: form.note || null,
      });
      toast.success("Dokument abgelegt");
      setForm({ category: CATEGORIES[0], title: "", issue_date: "", expiry_date: "", note: "" });
      setFile(null); setOpen(false); load();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); load(); };

  const grouped = CATEGORIES.map((c) => [c, items.filter((i) => i.category === c)]);

  return (
    <div>
      <PageHeader title="Dokumentenablage" subtitle="Pflicht-Dokumente mit Fristen-Erinnerung">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-add-document"><Plus className="h-4 w-4 mr-1" /> Dokument hinzufügen</Button></DialogTrigger>
          <DialogContent data-testid="document-dialog">
            <DialogHeader><DialogTitle>Dokument ablegen</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="select-doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Titel *</Label>
                <Input data-testid="input-doc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Belehrung Mustermann" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Ausgestellt am</Label>
                  <Input type="date" data-testid="input-doc-issue" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Gültig bis / Fällig</Label>
                  <Input type="date" data-testid="input-doc-expiry" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Datei (PDF/Bild)</Label>
                <label className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-teal-400">
                  <Upload className="h-4 w-4" /> {file ? file.name : "Datei auswählen"}
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="input-doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label></div>
              <div className="space-y-1.5"><Label>Bemerkung</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="optional" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button data-testid="btn-submit-document" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Ablegen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([cat, docs]) => (
            <div key={cat}>
              <h3 className="font-heading text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <FolderArchive className="h-4 w-4 text-teal-500" /> {cat}
                <span className="text-slate-400 font-normal">({docs.length})</span>
              </h3>
              {docs.length === 0 ? (
                <p className="text-sm text-slate-400 pl-6">Keine Dokumente</p>
              ) : (
                <div className="space-y-2">
                  {docs.map((d) => {
                    const ex = expiryInfo(d.expiry_date);
                    return (
                      <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="document-row">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-800 text-sm">{d.title}</div>
                          <div className="text-xs text-slate-500">{d.note || (d.issue_date ? `Ausgestellt: ${fmtDate(d.issue_date)}` : "—")}</div>
                        </div>
                        {ex && <StatusBadge variant={ex.variant}>{ex.text}</StatusBadge>}
                        <FileLink fileId={d.file_id} />
                        <button onClick={() => del(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
